import { Link } from "react-router-dom";
import { Sparkle, ArrowUpRight, Clock } from "lucide-react";
import ImageWithFallback from "./ImageWithFallback";
import { countdownLabel } from "../lib/countdown";

function fmtRange(w) {
    if (!w) return "";
    const d1 = new Date(w.start);
    const d2 = new Date(w.end);
    const opt = { month: "short", day: "numeric" };
    return `${d1.toLocaleDateString("en-US", opt)} – ${d2.toLocaleDateString("en-US", opt)}`;
}

export default function RecommendationCard({ rec, season = "this", testidPrefix = "rec" }) {
    const countdown = countdownLabel(rec.window);
    return (
        <Link
            to={`/recommendation/${rec.id}?season=${season}`}
            className="sp-card group block"
            data-testid={`${testidPrefix}-card-${rec.id}`}
        >
            <div className="relative aspect-[4/3] overflow-hidden">
                <ImageWithFallback
                    src={rec.image}
                    alt={rec.city}
                    accentColor={rec.planet_color}
                    imgClassName="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 sp-pill" data-testid={`${testidPrefix}-card-${rec.id}-planet`}>
                    <Sparkle strokeWidth={1.25} className="w-3 h-3" style={{ color: rec.planet_color }} />
                    {rec.planet}
                </div>
                <div className="absolute top-4 right-4 sp-pill" data-testid={`${testidPrefix}-card-${rec.id}-score`}>
                    {rec.score}<span className="text-ink-muted ml-1">/100</span>
                </div>
                {countdown && (
                    <div
                        className={`absolute bottom-4 left-4 sp-pill ${countdown.urgent ? "bg-ink text-sand-50 border-ink" : ""}`}
                        data-testid={`${testidPrefix}-card-${rec.id}-countdown`}
                    >
                        <Clock strokeWidth={1.5} className="w-3 h-3" />
                        {countdown.text}
                    </div>
                )}
                {rec.image_photographer && (
                    <div
                        className="absolute bottom-2 right-2 text-[9px] tracking-wide text-white/80 bg-black/35 backdrop-blur-sm px-2 py-0.5 rounded-full border border-white/10 pointer-events-none"
                        data-testid={`${testidPrefix}-card-${rec.id}-photo-credit`}
                    >
                        © {rec.image_photographer} · Pexels
                    </div>
                )}
            </div>
            <div className="p-6">
                <div className="sp-overline">{rec.planet_theme}</div>
                <h3 className="text-2xl font-serif mt-2 leading-tight">{rec.city}</h3>
                <div className="text-sm text-ink-muted">{rec.country}</div>
                {rec.recommendation_reason && (
                    <p
                        className="mt-3 text-xs italic text-ink-soft font-serif leading-snug line-clamp-2"
                        data-testid={`${testidPrefix}-card-${rec.id}-reason`}
                    >
                        {rec.recommendation_reason}
                    </p>
                )}
                <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="font-mono text-ink/80" data-testid={`${testidPrefix}-card-${rec.id}-window`}>{fmtRange(rec.window)}</span>
                    <ArrowUpRight strokeWidth={1.5} className="w-4 h-4 text-ink-muted group-hover:text-ink transition-colors" />
                </div>
                {rec.window?.confidence_score !== undefined && (
                    <div
                        className="mt-3 flex items-center gap-2 text-xs text-ink-muted"
                        data-testid={`${testidPrefix}-card-${rec.id}-confidence`}
                    >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: rec.planet_color }} />
                        Astrological confidence
                        <span className="ml-auto font-mono text-ink">{rec.window.confidence_score}%</span>
                    </div>
                )}
            </div>
        </Link>
    );
}
